import { useState, useRef, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { classNames } from '../../../utils/common'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'

const Carousel = ({ items }) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [translateX, setTranslateX] = useState(0)
  const containerRef = useRef(null)
  const hasMultipleItems = items.length > 1

  // Calcular índices visibles (anterior, actual, siguiente)
  const activeIndexes = useMemo(() => {
    if (!hasMultipleItems) return new Set([0])
    const total = items.length
    const prevIndex = (currentIndex - 1 + total) % total
    const nextIndex = (currentIndex + 1) % total
    return new Set([prevIndex, currentIndex, nextIndex])
  }, [hasMultipleItems, items.length, currentIndex])

  // Navegación
  const goToSlide = (index) => {
    setCurrentIndex(index)
  }

  const goToPrev = () => {
    if (!hasMultipleItems) return
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length)
  }

  const goToNext = () => {
    if (!hasMultipleItems) return
    setCurrentIndex((prev) => (prev + 1) % items.length)
  }

  // Manejo de eventos táctiles y mouse
  const handleDragStart = (e) => {
    if (!hasMultipleItems) return
    setIsDragging(true)
    const clientX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX
    setStartX(clientX)
    setTranslateX(0)
  }

  const handleDragMove = (e) => {
    if (!isDragging || !hasMultipleItems) return
    const clientX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX
    const diff = clientX - startX
    setTranslateX(diff)
  }

  const handleDragEnd = () => {
    if (!isDragging || !hasMultipleItems) return
    setIsDragging(false)

    // Si el desplazamiento es mayor a 50px, cambiar de slide
    if (Math.abs(translateX) > 50) {
      if (translateX > 0) {
        goToPrev()
      } else {
        goToNext()
      }
    }
    setTranslateX(0)
  }

  // Prevenir selección de texto durante el arrastre
  useEffect(() => {
    if (isDragging) {
      document.body.style.userSelect = 'none'
    } else {
      document.body.style.userSelect = ''
    }
    return () => {
      document.body.style.userSelect = ''
    }
  }, [isDragging])

  return (
    <div className="relative py-8">
      {/* Contenedor principal */}
      <div
        ref={containerRef}
        className="relative overflow-hidden"
        onMouseDown={handleDragStart}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
      >
        <div className="relative flex h-[19rem] w-full items-center justify-center sm:h-[19rem]">
          {items.map((item, index) => {
            const isActive = index === currentIndex
            const isSibling = hasMultipleItems && activeIndexes.has(index) && !isActive
            const isHidden = !isActive && !isSibling

            // Calcular posición
            let position = 0
            if (hasMultipleItems) {
              const diff = index - currentIndex
              if (diff === 1 || diff === -(items.length - 1)) {
                position = 1 // derecha
              } else if (diff === -1 || diff === items.length - 1) {
                position = -1 // izquierda
              }
            }

            const slideClass = classNames(
              'absolute left-1/2 top-1/2 flex w-[90%] max-w-md -translate-x-1/2 -translate-y-1/2 items-center justify-center transition-all duration-500 ease-out sm:w-[68%] lg:w-[48%] xl:w-[40%]',
              isActive && 'z-20 scale-100 opacity-100 drop-shadow-[0_20px_40px_rgba(15,23,42,0.15)]',
              isSibling && 'z-10 scale-95 opacity-40 blur-[1px]',
              isHidden && 'pointer-events-none z-0 scale-90 opacity-0'
            )

            // Aplicar desplazamiento durante el arrastre solo al slide activo
            // La tarjeta activa se eleva -20px respecto al centro
            const activeElevation = 'calc(-50% - 20px)'
            const dragStyle = isActive && isDragging
              ? { transform: `translate(calc(-50% + ${translateX}px), ${activeElevation})` }
              : isActive
                ? { transform: `translate(-50%, ${activeElevation})` }
                : position !== 0
                  ? { transform: `translate(calc(-50% + ${position * 40}%), -50%)` }
                  : {}

            return (
              <div
                className={slideClass}
                key={index}
                style={dragStyle}
              >
                <div className="w-full">
                  {item}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Botones de navegación */}
      {hasMultipleItems && (
        <>
          <button
            type="button"
            onClick={goToPrev}
            className="absolute left-2 top-1/2 z-30 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border-2 border-orange-500 bg-white text-orange-500 shadow-lg transition hover:bg-orange-500 hover:text-white"
            aria-label="Slide anterior"
          >
            <ChevronLeftIcon className="h-5 w-5 mr-0.5" strokeWidth={3.5} />
          </button>
          <button
            type="button"
            onClick={goToNext}
            className="absolute right-2 top-1/2 z-30 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border-2 border-orange-500 bg-white text-orange-500 shadow-lg transition hover:bg-orange-500 hover:text-white"
            aria-label="Siguiente slide"
          >
            <ChevronRightIcon className="h-5 w-5 ml-0.5" strokeWidth={3.5} />
          </button>
        </>
      )}

      {/* Indicadores (dots) */}
      {hasMultipleItems && (
        <div className="mt-8 flex w-full items-center justify-center gap-1">
          {items.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`Ir al slide ${index + 1}`}
              onClick={() => goToSlide(index)}
              className={classNames(
                'rounded-full transition-all duration-300',
                currentIndex === index ? 'h-3 w-3 bg-primary' : 'h-2 w-2 bg-orange-500'
              )}
            />
          ))}
        </div>
      )}
    </div>
  )
}

Carousel.propTypes = {
  items: PropTypes.array.isRequired
}

export default Carousel
